import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  syncPathname,
  ACTION_NAVIGATE_SYNC,
  type NavigateSyncResponse,
} from "@tiendanube/nexo";

import nexo from "./NexoClient";

// Synchronizes our internal route with the parent TiendaNube admin URL.
// Without this, navigating inside our app does not update the admin's URL/back
// button and TN considers us "stuck" at the install path. Mirrors the pattern
// in the Storydots template.
//
// 2026-05-12 fix: the parent admin frequently echoes a stale path back to us
// shortly after we navigate to a child route. Without filtering, "Correr
// análisis" from the dashboard tile (which navigates to /agents/:id/history?
// run=1) was clobbered moments later by an inbound `path: "/agents/:id"`
// echo that bounced us back to the profile and discarded `?run=1`, so the
// run trigger never fired. We now ignore inbound navigates that point to a
// strict ancestor of our current route within a 2-second window after our
// last outbound — that's the echo signature. Genuine parent-initiated
// "navigate up" (e.g., breadcrumb click) still works because it arrives
// after the echo window.
type NexoSyncRouteProps = {
  children: ReactElement;
  fallback?: ReactElement | null;
  onReady?: () => void;
};

export function NexoSyncRoute({
  children,
  fallback = null,
  onReady,
}: NexoSyncRouteProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lastOutboundAtRef = useRef<number>(0);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Outbound: tell the parent which path we just navigated to.
  useEffect(() => {
    lastOutboundAtRef.current = Date.now();
    syncPathname(nexo, pathname);
  }, [pathname]);

  // Inbound: parent admin asks us to navigate (e.g., breadcrumb click).
  useEffect(() => {
    const unsubscribe = nexo.suscribe(
      ACTION_NAVIGATE_SYNC,
      ({ path, replace }: NavigateSyncResponse) => {
        const currentPathname = window.location.pathname;
        const sameOrAncestor =
          currentPathname === path ||
          currentPathname.startsWith(path.endsWith("/") ? path : `${path}/`);
        const withinEchoWindow =
          Date.now() - lastOutboundAtRef.current < 2000;
        if (sameOrAncestor && withinEchoWindow) {
          // Echo of our own outbound — already at this path or a deeper
          // child of it. Skip to avoid clobbering child-route mount state.
          return;
        }
        if (replace) {
          navigate(path, { replace: true });
        } else {
          navigate(path);
        }
      },
    );
    onReady?.();
    setIsSubscribed(true);
    return unsubscribe;
  }, [navigate, onReady]);

  return isSubscribed ? children : fallback;
}

export default NexoSyncRoute;
